import { useEffect } from 'react';
import { Monitor, Moon, Sun, X } from 'lucide-react';
import { useTheme, type ThemeMode } from '../theme/ThemeContext';
import { useT } from '../I18nContext';

/**
 * Settings modal - D5c. Currently houses appearance only (Light /
 * Dark / Match system). Reuses the History-modal scrim styling so
 * the visual language is consistent across overlays.
 *
 * Opened from the sidebar UserMenu's "Settings" item.
 *
 * Future settings to add here:
 *   - Audio source (browser SpeechSynthesis vs server TTS)
 *   - Default response length (might lift from rail to settings
 *     so the rail feels less cluttered)
 *   - Per-user document filter defaults
 */
interface SettingsModalProps {
  onClose: () => void;
}

interface AppearanceOption {
  value: ThemeMode;
  label: string;
  hint: string;
  Icon: typeof Sun;
}

export function SettingsModal({ onClose }: SettingsModalProps) {
  const { mode, setMode } = useTheme();
  const t = useT();

  // Built at render-time so useT() resolves against the current
  // language. Module-level const would resolve at import time before
  // the I18nProvider has rendered.
  const APPEARANCE: ReadonlyArray<AppearanceOption> = [
    { value: 'light',  label: t('settings_modal.appearance_light_label'),  hint: t('settings_modal.appearance_light_hint'),  Icon: Sun },
    { value: 'dark',   label: t('settings_modal.appearance_dark_label'),   hint: t('settings_modal.appearance_dark_hint'),   Icon: Moon },
    { value: 'system', label: t('settings_modal.appearance_system_label'), hint: t('settings_modal.appearance_system_hint'), Icon: Monitor },
  ];

  // Esc to close.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="history-modal-scrim"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('settings_modal.title')}
    >
      <div
        className="history-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="history-modal-header">
          <h2 className="history-modal-title">{t('settings_modal.title')}</h2>
          <button
            type="button"
            className="history-modal-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="history-modal-body">
          <section className="settings-section">
            <h3 className="settings-section-title">{t('settings_modal.appearance_heading')}</h3>
            <div className="settings-option-list" role="radiogroup" aria-label={t('settings_modal.appearance_heading')}>
              {APPEARANCE.map((opt) => {
                const checked = mode === opt.value;
                const Icon = opt.Icon;
                return (
                  <label
                    key={opt.value}
                    className={
                      'settings-option' + (checked ? ' is-selected' : '')
                    }
                  >
                    <input
                      type="radio"
                      name="appearance"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setMode(opt.value)}
                      className="settings-option-radio"
                    />
                    <Icon size={18} className="settings-option-icon" aria-hidden />
                    <div className="settings-option-text">
                      <div className="settings-option-label">{opt.label}</div>
                      <div className="settings-option-hint">{opt.hint}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
